import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X, Moon, Sun, ArrowUpRight, ShieldCheck, ChevronDown } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import BrandLogo from "@/components/BrandLogo";
import { buildNavGroups } from "./navConfig";
import MobileMenu from "./MobileMenu";

/** Grouped top-nav: Platform · Intelligence · Investors · Community · About */
export default function Navbar() {
  const { t } = useTranslation();
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const nav = useNavigate();

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (!e.target.closest("[data-nav-group]")) setMenuOpen(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Top navbar stays visible at every scroll position — investors should
  // always have one tap back to the main IA. We use `fixed` instead of
  // `sticky` because some merged-page bodies introduce stacking contexts
  // (backdrop-filter / transform) that break sticky resolution.
  const groups = buildNavGroups(t);

  return (
    <header
      className="fixed inset-x-0 top-0 z-50 border-b border-border/60 bg-background/85 backdrop-blur-xl"
      data-testid="site-navbar"
    >
      <a href="#main-content" className="skip-to-content" data-testid="skip-to-content">
        Skip to main content
      </a>
      <div className="container-x flex h-16 items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo" aria-label="Circularity Global — Home">
          <BrandLogo variant="symbol" size={26} testId="nav-logo-symbol" />
          <BrandLogo variant="wordmark" size={26} testId="nav-logo-wordmark" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {groups.map((g) => (
            <div
              key={g.id}
              data-nav-group
              className="relative"
              onMouseEnter={() => setMenuOpen(g.id)}
              onMouseLeave={() => setMenuOpen(null)}
            >
              <button
                type="button"
                onClick={() => setMenuOpen(menuOpen === g.id ? null : g.id)}
                data-testid={`nav-${g.id}`}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm transition-colors ${
                  menuOpen === g.id ? "text-foreground bg-secondary/60" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {g.label} <ChevronDown size={13} className={`transition-transform ${menuOpen === g.id ? "rotate-180" : ""}`} />
              </button>
              {menuOpen === g.id && (
                <div className="absolute left-0 top-full pt-2 w-[320px]" data-testid={`nav-menu-${g.id}`}>
                  <div className="rounded-2xl border border-border bg-popover/95 backdrop-blur-xl shadow-2xl p-2">
                    {g.items.map(([to, label, desc]) => (
                      <Link
                        key={to}
                        to={to}
                        onClick={() => setMenuOpen(null)}
                        data-testid={`nav-link-${to.replace(/[/]+/g, "-").replace(/^-/, "")}`}
                        className="block rounded-xl px-3 py-2 hover:bg-secondary/60 transition-colors"
                      >
                        <p className="text-sm font-medium text-foreground">{label}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{desc}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={toggle}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            className="rounded-full border border-border bg-card/60 p-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {user ? (
            <div className="hidden md:flex items-center gap-2">
              {user.role === "admin" && (
                <button onClick={() => nav("/admin")} data-testid="nav-admin" className="btn-secondary !py-2 !px-4 text-xs">
                  <ShieldCheck size={12} /> {t("nav.admin")}
                </button>
              )}
              <button onClick={() => nav("/dashboard")} data-testid="nav-dashboard" className="btn-secondary !py-2 !px-4 text-xs">
                {t("nav.dashboard")}
              </button>
              <button onClick={() => nav("/portal")} data-testid="nav-portal" className="btn-secondary !py-2 !px-4 text-xs">
                {t("nav.portal")}
              </button>
              <button onClick={async () => { await logout(); nav("/"); }} data-testid="nav-logout" className="btn-ghost text-xs">
                {t("nav.logout")}
              </button>
            </div>
          ) : (
            <Link to="/login" data-testid="nav-login" className="hidden md:inline-flex btn-ghost text-xs">{t("nav.sign_in")}</Link>
          )}

          <Link to="/contact" data-testid="nav-join-network" className={`${user ? "hidden" : "hidden md:inline-flex"} btn-primary !py-2 !px-4 text-xs`}>
            {t("nav.join")} <ArrowUpRight size={14} />
          </Link>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
            className="lg:hidden rounded-full border border-border bg-card/60 p-2"
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      <MobileMenu
        open={open}
        groups={groups}
        user={user}
        logout={logout}
        nav={nav}
        onClose={() => setOpen(false)}
      />
    </header>
  );
}
